import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import TeamBadge from '@/components/base/TeamBadge';
import { getMatch, getTeam } from '@/utils/data';

const imageMap: Record<string, string> = {
  'm-1001': 'https://readdy.ai/api/search-image?query=Modern%20indoor%20basketball%20arena%20with%20dramatic%20orange%20spotlights%20beaming%20down%20onto%20a%20polished%20wooden%20court%2C%20dark%20blurred%20crowd%2C%20cinematic%20sports%20atmosphere%2C%20high%20contrast%2C%20moody%20lighting&width=1200&height=640&seq=bball-hero-lakers&orientation=landscape',
  'm-1005': 'https://readdy.ai/api/search-image?query=Bright%20basketball%20arena%20interior%20with%20warm%20red%20and%20amber%20lights%2C%20polished%20hardwood%20court%2C%20energetic%20crowd%20blurred%20in%20background%2C%20cinematic%20sports%20photography%2C%20vivid%20warm%20tones&width=1200&height=640&seq=bball-hero-cba&orientation=landscape',
  'm-1012': 'https://readdy.ai/api/search-image?query=Abstract%20dark%20basketball%20arena%20with%20golden%20spotlight%20beams%20and%20floating%20dust%20particles%2C%20empty%20court%2C%20dramatic%20moody%20sports%20photography%2C%20deep%20shadows%2C%20warm%20gold%20accents&width=1200&height=640&seq=bball-hero-lakers2&orientation=landscape',
};

const featuredIds = ['m-1001', 'm-1005', 'm-1012'];

export default function FeaturedCarousel() {
  const [index, setIndex] = useState(0);
  const slides = featuredIds.map((id) => getMatch(id)).filter(Boolean);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((v) => (v + 1) % featuredIds.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative rounded-lg overflow-hidden h-[340px] md:h-[420px]">
      {slides.map((match, i) => {
        if (!match) return null;
        const home = getTeam(match.homeTeamId);
        const away = getTeam(match.awayTeamId);
        const active = i === index;
        return (
          <div
            key={match.id}
            className={`absolute inset-0 transition-opacity duration-700 ${active ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
            aria-hidden={!active}
          >
            <img src={imageMap[match.id]} alt={`${home?.name} 对阵 ${away?.name}`} className="absolute inset-0 w-full h-full object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/55 to-black/30"></div>
            <div className="relative h-full flex flex-col justify-between p-5 md:p-8">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-primary-500 text-background-50 font-medium">
                  {match.status === 'live' && <i className="ri-live-line animate-pulse-live"></i>}
                  {match.status === 'live' ? '正在直播' : '焦点赛事'}
                </span>
                <span className="text-xs md:text-sm text-white/80">{match.leagueName} · {match.stage}</span>
              </div>

              <div className="w-full">
                <div className="flex items-center justify-center gap-5 md:gap-10 text-white">
                  <div className="flex flex-col items-center gap-2 w-24 md:w-32">
                    <TeamBadge team={home} size="lg" />
                    <span className="text-sm md:text-base font-semibold text-center leading-tight">{home?.name}</span>
                  </div>
                  <div className="flex items-center gap-3 md:gap-5">
                    <span className="font-heading font-extrabold text-4xl md:text-6xl tabular-nums">
                      {match.status === 'upcoming' ? '-' : match.homeScore}
                    </span>
                    <span className="text-white/50 text-2xl md:text-3xl font-heading">:</span>
                    <span className="font-heading font-extrabold text-4xl md:text-6xl tabular-nums">
                      {match.status === 'upcoming' ? '-' : match.awayScore}
                    </span>
                  </div>
                  <div className="flex flex-col items-center gap-2 w-24 md:w-32">
                    <TeamBadge team={away} size="lg" />
                    <span className="text-sm md:text-base font-semibold text-center leading-tight">{away?.name}</span>
                  </div>
                </div>

                <div className="mt-5 flex flex-col sm:flex-row items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-xs md:text-sm text-white/85">
                    <span className="flex items-center gap-1">
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line"></i></span>
                      {match.date} {match.time}
                    </span>
                    <span className="text-white/40">|</span>
                    <span>{match.status === 'live' ? match.progress : match.venue}</span>
                  </div>
                  <Link
                    to={`/detail/${match.id}`}
                    className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer"
                  >
                    查看比赛详情
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <div className="absolute bottom-4 right-5 z-20 flex items-center gap-2">
        {featuredIds.map((id, i) => (
          <button
            key={id}
            type="button"
            aria-label={`切换到第 ${i + 1} 场焦点赛事`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${i === index ? 'w-6 bg-primary-500' : 'w-3 bg-white/50 hover:bg-white/80'}`}
          ></button>
        ))}
      </div>
    </div>
  );
}